import { MainHubSubmitStakeError } from '../../../../saga/staking/types';
import type { CurrencyCode } from 'app/api/services/site-configs/types';

import { CURRENCY_SYMBOLS } from 'app/design-system/components/CurrencySymbol';
import { DictionaryData, isUseTranslationData, tFunction } from 'app/lang';

import {
  BET_ERROR_DICTIONARY, BetErrorCode,
  MATCH_ERROR_DICTIONARY, MatchErrorCode,
  OPERATOR_REFUSED_DICTIONARY, OperatorRefusedCode,
  STAKE_ERROR_DICTIONARY, StakeErrorCode,
  TypeOfError,
} from '../../../../constants';
import { RecordOfEntryErrorType } from '../../../../records';

export type ErrorMessage = DictionaryData | string | JSX.Element;

export function getErrorMessage(
  t: tFunction,
  message?: ErrorMessage,
){
  if (!message) {
    return null;
  }

  if (isUseTranslationData(message)) {
    return t(message);
  }

  return message;
}

type Dictionary = typeof STAKE_ERROR_DICTIONARY
                | typeof MATCH_ERROR_DICTIONARY
                | typeof BET_ERROR_DICTIONARY
                | typeof OPERATOR_REFUSED_DICTIONARY;

type Error= {
  code?: StakeErrorCode | MatchErrorCode | BetErrorCode;
  rejectReason?: MainHubSubmitStakeError['rejectReason'];
  currency?: CurrencyCode;
};

function getDataByCode(
  t: tFunction,
  dictionary: Dictionary,
  error?: Error,
): string | null {
  if (error?.rejectReason) {
    const reason = error.rejectReason.reason;
    const currency = error.currency ? CURRENCY_SYMBOLS[error.currency] : '';
    const acceptedAmount = error.rejectReason?.amount;
    const rejectedReasonMessage = reason === OperatorRefusedCode.READY_TO_ACCEPT_ONLY_AMOUNT
      ? t(dictionary[reason as keyof typeof dictionary], { acceptedAmount, currency })
      : t(dictionary[reason as keyof typeof dictionary]);

    return reason in dictionary ? rejectedReasonMessage : null;
  }

  const code = error?.code;
  return code && code in dictionary ? t(dictionary[code as keyof typeof dictionary]) : null;
}

export function getErrorMessageByType(
  t: tFunction,
  error?: RecordOfEntryErrorType | null,
  currency?: CurrencyCode,
) {
  if (!error) {
    return null;
  }

  const { code, type } = error;
  if (!code) {
    return null;
  }

  switch (type) {
    case TypeOfError.GENERAL:
      if (code === StakeErrorCode.BET_PRICE_CHANGED && error.rejectReason) {
        return getDataByCode(t, OPERATOR_REFUSED_DICTIONARY, {
          rejectReason: error.rejectReason,
          currency,
        });
      }

      return getDataByCode(t, STAKE_ERROR_DICTIONARY, { code });
    case TypeOfError.MATCH:
      return getDataByCode(t, MATCH_ERROR_DICTIONARY, { code });
    case TypeOfError.BET:
      return getDataByCode(t, BET_ERROR_DICTIONARY, { code });
    default:
      assertExhaustiveness(type);
      return null;
  }
}
